import { useCallback, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { WorkspaceContext } from "./workspaceContext";
import type { AppPath, PaneQuery, PaneQueryOptions, TabShows, Workspace } from "./workspaceTypes";

/** The workspace: open a route in this tab, a new tab or beside it, or close this tab (WA-10, F90). */
export function useWorkspace(): Workspace {
  const workspace = useContext(WorkspaceContext);
  if (workspace === null) {
    throw new Error("useWorkspace() needs the workspace owner (shell/workspace/, WA-10) above it.");
  }
  return workspace;
}

/** The page's own query (WA-10). Like `useSearchParams`, the setter keeps the path and drops the fragment. */
export function usePaneQuery(): PaneQuery {
  const [query, setSearchParams] = useSearchParams();
  const setQuery = useCallback(
    (next: URLSearchParams | Readonly<Record<string, string>>, options?: PaneQueryOptions) => {
      setSearchParams(new URLSearchParams(next), { replace: options?.replace === true });
    },
    [setSearchParams],
  );
  return [query, setQuery] as const;
}

/**
 * Replaces this tab's route without a history entry: an old address that now lives
 * elsewhere, once the page has read where (`/reviews/<id>` to its Thread step, ADR-0031 D4).
 */
export function usePaneRedirect(): (route: AppPath) => void {
  const navigate = useNavigate();
  return useCallback((route: AppPath) => { void navigate(route, { replace: true }); }, [navigate]);
}

/**
 * Names this tab after what the page shows, once the page knows it (F90). One rule, here (IM-SHELL):
 * the Thread's title, "Workflow: …" for a Workflow, the document's key, any other thing by its name.
 */
export function useTabTitle(shows: TabShows | null): void {
  const { t } = useTranslation();
  const { setTitle } = useWorkspace();
  let title = shows?.name ?? null;
  if (shows?.kind === "workflow") {
    title = t("shell.tabs.workflowTitle", { name: shows.name });
  } else if (shows?.kind === "preparation") {
    title = shows.name === "" ? shows.heading : t("shell.tabs.preparationTitle", { heading: shows.heading, name: shows.name });
  }
  useEffect(() => { setTitle(title); }, [setTitle, title]);
}
